import { Dashboard, DashboardDocument, DashboardScreenshot, DashboardSelection } from "../types/files";
import { SaveDashboardItemType } from "../types/common";
import React from "react";
import moment from "moment";

export const getDashboardItemType = (item: DashboardDocument | DashboardScreenshot | DashboardSelection): SaveDashboardItemType => {
    if ("searchTerms" in item) {
        return SaveDashboardItemType.Page;
    }
    if ("selectedText" in item) {
        return SaveDashboardItemType.Text;
    }

    return SaveDashboardItemType.ScreenCapture;
}


export const scaleUpCommentBullet = (commentIndex: number) => {
    const scaledUp = document.getElementsByClassName('image-marker-scale-up');
    for (let i = 0; i < scaledUp.length; i++) {
        scaledUp[i].classList.remove("image-marker-scale-up");
    }
    const bulletElem = document.querySelectorAll(`[data-bulletmarkerid='${commentIndex}']`);
    for (let i = 0; i < bulletElem.length; i++) {
        if (!bulletElem[i].classList.contains("image-marker-scale-up-selected")) {
            bulletElem[i].classList.add("image-marker-scale-up");
        }
    }
}

export const scrollDashboardElementIntoView = (
    scrollRef: React.MutableRefObject<HTMLDivElement | null>,
    orderNumber: number
) => {
    const scrollElement = scrollRef.current?.children[orderNumber];
    if (scrollElement) {
        window.scrollBy(1, 1);
        scrollElement.scrollIntoView({inline: "start", behavior: 'smooth'});
        setTimeout(()=>{
            if (scrollRef) {
                scrollRef!.current?.scrollBy({
                    top: 0,
                    left: -7,
                    behavior : "smooth"
                });
            }
        }, 1500);
    }
}

export const darkenScrollArrows = (dark: boolean) => {
    const scrollArrows = document.getElementsByClassName('MuiFab-root');
    Array.from(scrollArrows).forEach((element) => {
        (element as HTMLElement).style.opacity=dark ? "0.2": "1";
    });
}

export const isDashboardNew = (dashboard: Dashboard) => {
    const diffHours = moment(dashboard.createdAt).diff(moment.now(), 'hours');
    return diffHours > -6;
}

export const scrollbarStyle = {
    "&::-webkit-scrollbar": {
        width: 0,
        height: 0,
        background: 'none',
    },
    "&::-webkit-scrollbar-track": {
        background: 'none'
    },
    "&::-webkit-scrollbar-thumb": {
        visibility: 'hidden',
    },
    "&:hover": {
        "&::-webkit-scrollbar-thumb": {
            visibility: 'hidden',
        }
    }
}

export const ChromeExtensionLink = "https://chromewebstore.google.com/detail/notissia-screenshot-captu/hnhfbkifbdghfaomkfmopoefmmafhelb";

export const isTitleDefault = (title?: string | null) => !title || title.match(/Page \d+$/gi)?.[0];

export const formatMetricValue = (raw?: string | null, unit?: string | null): string => {
    if (raw === null || raw === undefined || raw === '') return '—';
    const cleanInput = String(raw).replace(/[^0-9bmkt.$%-]/gi, '').toLowerCase();
    let multiplier = 1;
    if (cleanInput.includes('t')) multiplier = 1e12;
    else if (cleanInput.includes('b')) multiplier = 1e9;
    else if (cleanInput.includes('m')) multiplier = 1e6;
    else if (cleanInput.includes('k')) multiplier = 1e3;
    const numeric = multiplier * parseFloat(cleanInput.replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(numeric)) return String(raw);
    if (unit === '$') return `${numeric < 0 ? '-' : ''}$${Math.abs(numeric).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    if (unit === '%') return `${numeric.toFixed(1)}%`;
    return numeric.toLocaleString();
};

export const defaultStatuses = [
    'new',
    'done',
    'passed',
    'in progress',
];

export const defaultStages = [
    'Pre-Seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C',
    'Series D',
    'Late Stage',
];

const ASSET_CLASS_LABELS: Record<string, string> = {
    venture: 'Venture',
    private_equity: 'Private Equity',
    private_credit: 'Private Credit',
    real_estate: 'Real Estate',
    other: 'Other',
};

export function formatAssetClassLabel(assetClass?: string | null): string {
    if (!assetClass?.trim()) return '—';
    const normalized = assetClass.trim().toLowerCase();
    if (ASSET_CLASS_LABELS[normalized]) return ASSET_CLASS_LABELS[normalized];
    return normalized
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export type AssetClassColors = { bg: string; text: string; border: string };

const ASSET_CLASS_COLORS: Record<string, AssetClassColors> = {
    venture: { bg: '#F0FDFA', text: '#0D9488', border: '#99F6E4' },
    private_equity: { bg: '#FFF7ED', text: '#EA580C', border: '#FED7AA' },
    private_credit: { bg: '#EFF6FF', text: '#2563EB', border: '#BFDBFE' },
    real_estate: { bg: '#FFFBEB', text: '#D97706', border: '#FDE68A' },
    other: { bg: '#F9FAFB', text: '#4B5563', border: '#E5E7EB' },
};

const DEFAULT_ASSET_CLASS_COLORS: AssetClassColors = {
    bg: '#F9FAFB',
    text: '#4B5563',
    border: '#E5E7EB',
};

export function getAssetClassColors(assetClass?: string | null): AssetClassColors {
    if (!assetClass?.trim()) return DEFAULT_ASSET_CLASS_COLORS;
    const normalized = assetClass.trim().toLowerCase();
    return ASSET_CLASS_COLORS[normalized] ?? DEFAULT_ASSET_CLASS_COLORS;
}

export const defaultGeographies = [
    'US - all',
    'Europe',
    'Latin America',
    'Asia',
    'Africa',
];

export const defaultTags = [
    'Advertising',
    'Aerospace',
    'AgTech',
    'AI',
    'Analytics',
    'AR/VR',
    'AutoTech',
    'B2B',
    'B2C',
    'BioTech',
    'Chemicals',
    'Climate',
    'ClimateTech/CleanTech',
    'Cloud Infrastructure',
    'ConstructionTech',
    'Consumer',
    'Consumer Health',
    'Consumer Internet',
    'Cosmetics',
    'Creator Economy',
    'Crypto',
    'Cyber',
    'Cybersecurity',
    'Data Services',
    'Defense',
    'DeepTech',
    'DevTools',
    'Digital Health',
    'Direct-to-consumer (DTC)',
    'E-Commerce',
    'Education',
    'EnergyTech',
    'Enterprise',
    'Enterprise Applications',
    'Enterprise Infrastructure',
    'Entertainment & Sports',
    'Fashion',
    'FinTech',
    'Food & Beverage',
    'Future of Work',
    'Games',
    'GovTech',
    'Hardware',
    'Health',
    'Hospitality',
    'HRTech',
    'Impact',
    'Infrastructure',
    'Insurance',
    'IoT',
    'LegalTech',
    'Logistics',
    'Manufacturing',
    'Marketplace',
    'MarketingTech',
    'Media/Content',
    'Medical Devices',
    'Payments',
    'Proptech',
    'Retail',
    'Robotics',
    'Security',
    'Space',
    'Supply Chain Tech',
    'Therapeutics',
    'Travel',
    'Web3',
    'Wellness & Fitness',
];

export const extendedTags = [
    '3D Printing',
    'Accounting',
    'Acoustics',
    'Addiction Recovery',
    'Adhesive',
    'AdTech',
    'AgTech',
    'AI',
    'Air',
    'Alcohol',
    'Algae',
    'Alt Protein',
    'Art',
    'Automotive',
    'Autonomous',
    'Aviation',
    'B2B',
    'B2C',
    'Baby',
    'Batteries',
    'Beauty',
    'Beverage',
    'BHY',
    'Biotech',
    'Blockchain',
    'Boating',
    'Cameras',
    'Camping',
    'Cannabis',
    'Cargo Insurance',
    'Chemicals',
    'City Planning',
    'Claims',
    'ClimateTech',
    'Clothing',
    'Cloud',
    'Coffee',
    'Commercial Real Estate',
    'Compliance',
    'Construction',
    'Consumer',
    'Consumer Behavior',
    'Consumer Hardware',
    'Coworking',
    'CPG',
    'Creator Economy',
    'Crops',
    'Cross Border',
    'Currency',
    'CyberTech',
    'Data Analytics',
    'Data Visualization',
    'Dating',
    'Debt',
    'DeepTech',
    'Dental',
    'Developer Tool',
    'Diagnostics',
    'Digital Transformation',
    'Drones',
    'EDGE',
    'EdTech',
    'Elderly',
    'Electrification',
    'Embedded',
    'Employee Benefits',
    'Employee Engagement',
    'Energy',
    'Enterprise',
    'Events',
    'Facial Recognition',
    'Fantasy Sports',
    'Farmers Market',
    'Farming',
    'Fashion',
    'FemTech',
    'Fermentation',
    'Fintech',
    'Fishing',
    'Fitness',
    'Food Delivery',
    'Foodservice',
    'FoodTech',
    'Footwear',
    'Fraud Prevention',
    'Furniture',
    'Gaming',
    'Gig Economy',
    'Golf',
    'GovTech',
    'Health Care',
    'Health Insurance',
    'Higher Education',
    'Home Improvement',
    'Home Security',
    'Hospitality',
    'Hotel',
    'HRTech',
    'Hydrogen',
    'IIOT',
    'Industrial',
    'Industry 4.0',
    'Ingredients',
    'InsuranceTech',
    'IoT',
    'Jewelry',
    'Language Learning',
    'Last Mile',
    'Law Enforcement',
    'LegalTech',
    'Life',
    'Livestock',
    'Logistics',
    'Manufacturing',
    'Marketplace',
    'MarTech',
    'Martial Arts',
    'Materials',
    'Meal Kits',
    'MediaTech',
    'Medical Device',
    'Mental Health',
    'Mesh Networks',
    'Metaverse',
    'MGA',
    'Military',
    'Mining',
    'ML',
    'Mobility',
    'MusicTech',
    'Navigation',
    'Neural Networks',
    'News',
    'Non-Profit',
    'Nurition',
    'Oil and Gas',
    'P&C',
    'Painting',
    'Parametrics',
    'Parenting Tech',
    'Payments',
    'Personal Care',
    'Personal Coaching',
    'Personal Injury',
    'PetTech',
    'Pharma',
    'Photos',
    'Plastics',
    'Policy Management',
    'PR',
    'Precision Agriculture',
    'Privacy',
    'Procurement',
    'PropTech',
    'Publishing',
    'Quantum Computers',
    'Railroad',
    'Recycling',
    'Regenerative Farming',
    'RegTech',
    'Regulatory',
    'Reinsurance',
    'Restaurants',
    'RetailTech',
    'Risk Analytics',
    'Robotics',
    'Safety',
    'SalesTech',
    'Satellite',
    'Semiconductors',
    'Sensors',
    'SexTech',
    'Skincare',
    'Smart Buildings',
    'Smart City',
    'Smart Homes',
    'Smart Lock',
    'SMB',
    'Social Media',
    'Social Shopping',
    'Soil',
    'Solar',
    'SpaceTech',
    'Sporting Goods',
    'SportsTech',
    'Steel',
    'Supply Chain',
    'Talent Management',
    'Telecom',
    'Telehealth',
    'Traceability',
    'Transportation',
    'Travel',
    'Treasury Management',
    'Trucking',
    'Utilities',
    'Vertical Farming',
    'Veteran',
    'Vitamin',
    'Voice',
    'VR/AR',
    'Water',
    'Wealth Management',
    'Wearables',
    'Wellness',
    'Workflow Management',
];

export const defaultKeyPeopleTags = [
    "Female",
    "Immigrant",
    "Second Time Founder",
    "Technical Background",
];